/**
 * A two-pane split with a draggable divider.
 *
 * The divider is a real `separator` widget, not a decorative bar: it takes
 * focus, responds to arrow keys, and reports its position through
 * `aria-valuenow`. Resizing a layout is exactly the kind of thing that quietly
 * becomes mouse-only if nobody insists otherwise.
 */
import { useCallback, useRef } from 'react';
import type { ReactNode } from 'react';

export interface SplitPaneProps {
  readonly first: ReactNode;
  readonly second: ReactNode;
  /** Fraction of the container given to the first pane, 0.2 to 0.8. */
  readonly ratio: number;
  readonly onRatioChange: (ratio: number) => void;
  readonly orientation?: 'horizontal' | 'vertical';
  readonly handleLabel: string;
  readonly className?: string;
}

const MIN_RATIO = 0.2;
const MAX_RATIO = 0.8;
const KEY_STEP = 0.02;

export function SplitPane({
  first,
  second,
  ratio,
  onRatioChange,
  orientation = 'horizontal',
  handleLabel,
  className,
}: SplitPaneProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const dragging = useRef(false);
  const vertical = orientation === 'vertical';

  const clamp = (value: number): number => Math.min(MAX_RATIO, Math.max(MIN_RATIO, value));

  const updateFromPointer = useCallback(
    (clientX: number, clientY: number) => {
      const element = containerRef.current;
      if (!element) return;
      const rect = element.getBoundingClientRect();
      const next = vertical
        ? (clientY - rect.top) / rect.height
        : (clientX - rect.left) / rect.width;
      onRatioChange(clamp(next));
    },
    [onRatioChange, vertical],
  );

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    const decrease = vertical ? 'ArrowUp' : 'ArrowLeft';
    const increase = vertical ? 'ArrowDown' : 'ArrowRight';
    if (event.key === decrease) onRatioChange(clamp(ratio - KEY_STEP));
    else if (event.key === increase) onRatioChange(clamp(ratio + KEY_STEP));
    else if (event.key === 'Home') onRatioChange(MIN_RATIO);
    else if (event.key === 'End') onRatioChange(MAX_RATIO);
    else if (event.key === 'Enter') onRatioChange(0.5);
    else return;
    event.preventDefault();
  };

  const percent = `${(ratio * 100).toFixed(3)}%`;

  return (
    <div
      ref={containerRef}
      className={`flex min-h-0 min-w-0 ${vertical ? 'flex-col' : 'flex-row'} ${className ?? ''}`}
    >
      <div
        className="min-h-0 min-w-0 overflow-hidden"
        style={vertical ? { height: percent } : { width: percent }}
      >
        {first}
      </div>

      <div
        role="separator"
        tabIndex={0}
        aria-orientation={vertical ? 'horizontal' : 'vertical'}
        aria-label={handleLabel}
        aria-valuenow={Math.round(ratio * 100)}
        aria-valuemin={Math.round(MIN_RATIO * 100)}
        aria-valuemax={Math.round(MAX_RATIO * 100)}
        className={[
          'group relative shrink-0 bg-border transition-colors',
          'hover:bg-accent focus-visible:bg-accent',
          vertical ? 'h-px w-full cursor-row-resize' : 'h-full w-px cursor-col-resize',
        ].join(' ')}
        onKeyDown={handleKeyDown}
        onPointerDown={(event) => {
          dragging.current = true;
          event.currentTarget.setPointerCapture(event.pointerId);
          event.preventDefault();
        }}
        onPointerMove={(event) => {
          if (dragging.current) updateFromPointer(event.clientX, event.clientY);
        }}
        onPointerUp={(event) => {
          dragging.current = false;
          event.currentTarget.releasePointerCapture(event.pointerId);
        }}
        onDoubleClick={() => onRatioChange(0.5)}
      >
        {/* A 1px line is impossible to grab; this widens the hit area without
            widening the visual. */}
        <span
          aria-hidden="true"
          className={
            vertical
              ? 'absolute inset-x-0 -top-2 -bottom-2 block'
              : 'absolute inset-y-0 -left-2 -right-2 block'
          }
        />
      </div>

      <div className="min-h-0 min-w-0 flex-1 overflow-hidden">{second}</div>
    </div>
  );
}
